import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  Edit2,
  Trash2,
  Copy,
  UserPlus,
  Hash,
  Loader2,
  Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentsPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [students, setStudents] = useState([]);
  const [teacherCode, setTeacherCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', grade_level: '', pin: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [studentsRes, codeRes] = await Promise.all([
        axios.get(`${API}/students`, { headers }),
        axios.get(`${API}/teacher/code`, { headers })
      ]);
      setStudents(studentsRes.data);
      setTeacherCode(codeRes.data.teacher_code);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // PIN required on create; on edit, only validate if user typed one
    if (!editingStudent || formData.pin) {
      if (formData.pin.length !== 4 || !/^\d{4}$/.test(formData.pin)) {
        toast.error('PIN must be exactly 4 digits');
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const headers = getAuthHeader();
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name || null,
        grade_level: formData.grade_level || null,
      };
      // Only send pin when present (on edit, empty pin = keep existing)
      if (formData.pin) payload.pin = formData.pin;
      
      if (editingStudent) {
        await axios.put(`${API}/students/${editingStudent.id}`, payload, { headers });
        toast.success('Student updated');
      } else {
        await axios.post(`${API}/students`, payload, { headers });
        toast.success('Student added');
      }
      
      fetchData();
      closeModal();
    } catch (error) {
      console.error('Error saving student:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Student limit reached. Please upgrade your plan.', {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/pricing')
          }
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save student');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (studentId) => {
    if (!confirm('Are you sure you want to delete this student?')) return;
    
    try {
      await axios.delete(`${API}/students/${studentId}`, {
        headers: getAuthHeader()
      });
      setStudents(students.filter(s => s.id !== studentId));
      toast.success('Student deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete student');
    }
  };

  const openAddModal = () => {
    setFormData({ first_name: '', last_name: '', grade_level: '', pin: '' });
    setEditingStudent(null);
    setShowAddModal(true);
  };

  const openEditModal = (student) => {
    setFormData({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      grade_level: student.grade_level || '',
      pin: ''
    });
    setEditingStudent(student);
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingStudent(null);
    setFormData({ first_name: '', last_name: '', grade_level: '', pin: '' });
  };

  const copyTeacherCode = async () => {
    try {
      await navigator.clipboard.writeText(teacherCode);
      toast.success('Teacher code copied!');
    } catch (error) {
      console.error('Clipboard error:', error);
      const textArea = document.createElement('textarea');
      textArea.value = teacherCode;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Teacher code copied!');
      } catch (e) {
        toast.info(`Teacher code: ${teacherCode}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const filteredStudents = students.filter(s => {
    const haystack = [
      s.first_name,
      s.last_name || '',
      s.grade_level || ''
    ].join(' ').toLowerCase();
    return haystack.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
              Students
            </h1>
            <p className="text-muted-foreground">
              Manage your students and share your teacher code
            </p>
          </div>
          <Button
            onClick={openAddModal}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-student-btn"
          >
            <UserPlus className="w-4 h-4" />
            Add Student
          </Button>
        </motion.div>

        {/* Teacher Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="border-border bg-primary/5">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Hash className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Your Teacher Code</p>
                    <p className="text-2xl font-mono font-bold text-primary tracking-wider" data-testid="teacher-code">
                      {teacherCode}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={copyTeacherCode}
                  className="gap-2"
                  data-testid="copy-code-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy Code
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Share this code with your students so they can log in at <span className="font-medium">/student/login</span>
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-students"
            />
          </div>
        </motion.div>

        {/* Students Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="border-border">
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredStudents.map((student, index) => (
                <motion.div
                  key={student.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="border-border hover:border-primary/30 transition-colors" data-testid={`student-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                            <span className="text-lg font-semibold text-primary">
                              {student.first_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground">
                              {student.first_name}{student.last_name ? ` ${student.last_name}` : ''}
                            </h3>
                            {student.grade_level && (
                              <p className="text-xs text-muted-foreground">{student.grade_level}</p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              Added {new Date(student.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(student)}
                            data-testid={`edit-student-${index}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(student.id)}
                            className="hover:text-destructive"
                            data-testid={`delete-student-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-muted-foreground" />
            </div>
            {searchQuery ? (
              <>
                <h3 className="font-serif text-xl font-medium text-primary mb-2">
                  No students found
                </h3>
                <p className="text-muted-foreground mb-6">
                  Try a different search term
                </p>
                <Button variant="outline" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              </>
            ) : (
              <>
                <h3 className="font-serif text-xl font-medium text-primary mb-2">
                  No students yet
                </h3>
                <p className="text-muted-foreground mb-6">
                  Add your first student to get started
                </p>
                <Button onClick={openAddModal} className="gap-2">
                  <UserPlus className="w-4 h-4" />
                  Add Your First Student
                </Button>
              </>
            )}
          </motion.div>
        )}
      </main>

      {/* Add/Edit Student Modal */}
      <Dialog open={showAddModal} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingStudent ? 'Edit Student' : 'Add New Student'}
            </DialogTitle>
            <DialogDescription>
              {editingStudent 
                ? 'Update student information. Leave PIN empty to keep current.'
                : 'Enter student details. They will use this to log in.'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                placeholder="Enter first name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="h-12"
                required
                data-testid="student-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                id="lastName"
                placeholder="Enter last name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="h-12"
                data-testid="student-lastname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gradeLevel">Grade Level <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                id="gradeLevel"
                placeholder="e.g. 3rd Grade, 10th Grade, K"
                value={formData.grade_level}
                onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                className="h-12"
                data-testid="student-grade-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pin">
                4-Digit PIN {editingStudent && '(leave empty to keep current)'}
              </Label>
              <Input
                id="pin"
                type="password"
                placeholder="••••"
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                className="h-12 tracking-widest"
                maxLength={4}
                required={!editingStudent}
                data-testid="student-pin-input"
              />
              <p className="text-xs text-muted-foreground">Must be exactly 4 digits</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-student-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingStudent ? 'Update Student' : 'Add Student'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentsPage;
